import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import {
  clearAuthReturnUrl,
  clearStoredAuthTokens,
  isJwtExpired,
  storeAuthReturnUrl,
} from './token.util';

/** Auth0 / OIDC errors that are resolved by sending the user through login again. */
export function isAuthRecoverableError(error: unknown): boolean {
  const err = error as {
    error?: string;
    message?: string;
    error_description?: string;
  };
  const code = err?.error ?? '';
  const text = `${err?.message ?? ''} ${err?.error_description ?? ''}`.toLowerCase();

  if (
    code === 'consent_required' ||
    code === 'login_required' ||
    code === 'missing_refresh_token' ||
    code === 'invalid_grant'
  ) {
    return true;
  }

  return (
    text.includes('consent required') ||
    text.includes('login required') ||
    text.includes('missing refresh token')
  );
}

export function authErrorNeedsConsentPrompt(error: unknown): boolean {
  const err = error as { error?: string; message?: string };
  const code = err?.error ?? '';
  const msg = (err?.message ?? '').toLowerCase();
  return (
    code === 'consent_required' ||
    code === 'missing_refresh_token' ||
    msg.includes('consent required') ||
    msg.includes('missing refresh token')
  );
}

/** Read the API error string from an HttpErrorResponse body. */
export function getApiErrorMessage(error: unknown): string {
  const body = (error as { error?: unknown })?.error;
  if (typeof body === 'string') {
    return body;
  }
  if (body && typeof body === 'object' && 'error' in body) {
    const msg = (body as { error?: unknown }).error;
    return typeof msg === 'string' ? msg : '';
  }
  return '';
}

/** True when the API rejected the bearer token (session expired / invalid). */
export function isInvalidTokenApiError(error: unknown): boolean {
  return getApiErrorMessage(error).toLowerCase() === 'invalid token';
}

/** API rejected the session (expired or invalid token). */
export function isHttpUnauthorized(error: unknown): boolean {
  return (error as { status?: number })?.status === 401;
}

function currentReturnTarget(): string {
  return (window.location.pathname + window.location.search).replace(/\/+$/, '') || '/';
}

/**
 * Ensures loginWithRedirect runs at most once per page load to avoid redirect loops.
 */
@Injectable({ providedIn: 'root' })
export class AuthRedirectGuard {
  private redirectInProgress = false;

  constructor(
    private auth: AuthService,
    private router: Router
  ) {}

  hasExpiredStoredTokens(): boolean {
    const accessToken = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');

    if (!accessToken && !idToken) {
      return false;
    }

    return isJwtExpired(accessToken) || isJwtExpired(idToken);
  }

  redirectToLogin(_error?: unknown): void {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;
    clearStoredAuthTokens();

    const target = currentReturnTarget();
    storeAuthReturnUrl(target);

    this.auth.loginWithRedirect({
      appState: { target },
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.audience,
      },
    });
  }

  restoreReturnUrlAfterLogin(target: string): void {
    if (!target || target === '/') {
      clearAuthReturnUrl();
      return;
    }

    clearAuthReturnUrl();
    this.router.navigateByUrl(target, { replaceUrl: true });
  }

  redirectForTokenError(error: unknown): void {
    if (this.redirectInProgress) {
      return;
    }

    if (!isAuthRecoverableError(error) && !isHttpUnauthorized(error)) {
      console.error('Auth token error (not redirecting):', error);
      return;
    }

    clearStoredAuthTokens();
    this.redirectInProgress = true;

    const needsConsent = authErrorNeedsConsentPrompt(error);
    const target = currentReturnTarget();
    storeAuthReturnUrl(target);

    this.auth.loginWithRedirect({
      appState: { target },
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.audience,
        ...(needsConsent ? { prompt: 'consent' as const } : {}),
      },
    });
  }
}
